import { RefObject, useEffect, useRef, useState } from "react";

/**
 * Monitors the first child element inside `wrapperRef` for position changes
 * via a `requestAnimationFrame` loop. Returns a key that increments each
 * time the element's screen position (x/y) changes.
 *
 * Only active while `enabled` is true — pass `false` to skip monitoring
 * entirely (zero overhead).
 *
 * Useful for forcing remounts or re-renders when layout shifts move an
 * element but no resize/scroll event fires (e.g. sibling content changes).
 *
 * @example
 * // Force a component to remount on reposition:
 * <Component key={repositionKey} />
 *
 * @example
 * // React to position changes in an effect:
 * useEffect(() => { ... }, [repositionKey]);
 */
export const useRepositionKey = (
	wrapperRef: RefObject<HTMLElement>,
	enabled: boolean,
) => {
	const [key, setKey] = useState(0);
	const lastRectRef = useRef<{ x: number; y: number } | null>(null);

	useEffect(() => {
		const wrapper = wrapperRef.current;
		const el = wrapper?.firstElementChild as HTMLElement | null;
		if (!enabled || !el) {
			return;
		}

		let rafId: number;
		const check = () => {
			const rect = el.getBoundingClientRect();
			const prev = lastRectRef.current;

			if (prev && (prev.x !== rect.x || prev.y !== rect.y)) {
				lastRectRef.current = { x: rect.x, y: rect.y };
				setKey((k) => k + 1);
			} else {
				lastRectRef.current = { x: rect.x, y: rect.y };
			}

			rafId = requestAnimationFrame(check);
		};
		rafId = requestAnimationFrame(check);

		return () => cancelAnimationFrame(rafId);
	}, [enabled, wrapperRef]);

	return key;
};
